import React, { useEffect, useState, useRef, useContext } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { UIContext } from "../../providers/UIProvider";
import { useData } from "../../providers/DataProvider";
import httpClient from "../../utils/httpClient";
import { downloadFile } from "../../utils/downloadFile";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button,
  LinearProgress,
  Typography,
  Box,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import { CompanyProps, RegionProps } from "../../providers/types";

type CMData = {
  employeeNumber: string;
  cm: string;
  bonusPercent: number;
  bonusAmount: number;
  bonusType: string;
};

type JobData = {
  jobId: number;
  areaID: number;
  areaName: string;
  projectNumber: string;
  subdivision: string;
  stage5Bonus: number;
  closingBonus: number;
  jobNumber: string;
  salesPrice: number;
  jobAddress: string;
  closeDate: string;
  stageCode: string;
  stage5Date: string;
  bonusStatusId: number;
  cMs: CMData[];
  notes: string;
  company?: string;
  companyID: number;
  regionID: number;
  regionName?: string;
};

type FilterOptions = {
  companies: CompanyProps[];
  regions: RegionProps[];
};

type DateFilterType = 'none' | 'month' | 'dateRange' | 'before' | 'after';

type PayrollSearchFilters = {
  companyId: number;
  regionId: number | "All" | '';
  dateFilterType: DateFilterType;
  month: string;
  startDate: string;
  endDate: string;
  beforeDate: string;
  afterDate: string;
};

const formatDateTime = (): string => {
  const now = new Date();
  return now.toISOString().slice(0, 16).replace(/[-T]/g, "").replace(/:/g, "");
};

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);
};

type UploadModalProps = {
  open: boolean;
  file: File | null;
  uploading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

const UploadModal = ({ open, file, uploading, onConfirm, onCancel }: UploadModalProps) => (
  <Dialog open={open} onClose={uploading ? undefined : onCancel} maxWidth="sm" fullWidth>
    <DialogTitle>Confirm File Upload</DialogTitle>
    <DialogContent>
      {file && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            File Details:
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Name: {file.name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Size: {(file.size / 1024).toFixed(2)} KB
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Type: {file.type || 'application/xlsx'}
          </Typography>
          
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Uploading file...
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} disabled={uploading}>
        Cancel
      </Button>
      <Button 
        onClick={onConfirm} 
        variant="contained" 
        color="primary"
        disabled={uploading}
      >
        {uploading ? 'Uploading...' : 'Upload'}
      </Button>
    </DialogActions>
  </Dialog>
);

export default function Payroll() {
  const { role, bonusType } = useData();
  const { accounts } = useMsal();
  const [state, dispatch] = useContext<any>(UIContext);
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingReport, setLoadingReport] = useState<boolean>(false);
  const [rows, setRows] = useState<JobData[]>([]);
  const [filteredRows, setFilteredRows] = useState<JobData[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  // Filter states
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    companies: [],
    regions: []
  });
  const [filters, setFilters] = useState<PayrollSearchFilters>({
    companyId: 0,
    regionId: 0,
    dateFilterType: 'none',
    month: '',
    startDate: '',
    endDate: '',
    beforeDate: '',
    afterDate: ''
  });
  const [showFilters, setShowFilters] = useState<boolean>(false);
  
  // Selection states
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState<boolean>(false);

  const isPayroll = accounts[0].idTokenClaims?.roles?.includes("Payroll");

  // Generate unique key for each row (jobId + employeeNumber + cm name)
  const getRowKey = (job: JobData, cm: CMData): string => {
    return `${job.jobId}-${cm.employeeNumber}-${cm.cm}`;
  };

  // Load filter options
  const loadFilterOptions = async () => {
    try {
      // Adjust these endpoints based on your actual API
      const [companiesRes, regionsRes] = await Promise.all([
        httpClient.get<CompanyProps[]>("/api/cba/companies"),
        httpClient.get<RegionProps[]>("/api/cba/regions")
      ]);
      
      setFilterOptions({
        companies: companiesRes?.data || [],
        regions: regionsRes?.data || []
      });
    } catch (error) {
      console.error("Failed to load filter options:", error);
      // Set empty arrays if API calls fail
      setFilterOptions({
        companies: [],
        regions: []
      });
    }
  };

  // Apply filters to rows
  const applyFilters = () => {
    let filtered = [...rows];
    filtered = filtered.filter(row => row.cMs && row.cMs.length > 0);

    // Filter by company - find the company name from the selected ID
    if (filters.companyId) {
      const selectedCompany = filterOptions.companies.find(c => c.companyId === filters.companyId);
      if (selectedCompany) {
        // Match by CompanyID (which should match companyId or companyName)
        filtered = filtered.filter(row => 
          row.companyID === selectedCompany.companyId
        );
      }
    }

    // Filter by region - find the region name from the selected ID
    if (filters.regionId) {
      if (filters.regionId === 'All') {
        // 'All' means no filtering by region
        // Don't filter
      } else {
        const selectedRegion = filterOptions.regions.find(r => r.regionId === filters.regionId);
        if (selectedRegion) {
          // Match by regionName or areaName
          filtered = filtered.filter(row => 
            row.regionID === selectedRegion.regionId
          );
        }
      }
    }

    // Filter by date
    const dateToUse = bonusType === "Stage5Bonus" ? 'stage5Date' : 'closeDate';
    
    if (filters.dateFilterType === 'month' && filters.month) {
      const [year, month] = filters.month.split('-');
      filtered = filtered.filter(row => {
        const rowDate = new Date(row[dateToUse]);
        return rowDate.getFullYear() === parseInt(year) && 
               rowDate.getMonth() === parseInt(month) - 1;
      });
    } else if (filters.dateFilterType === 'dateRange' && filters.startDate && filters.endDate) {
      const start = new Date(filters.startDate);
      const end = new Date(filters.endDate);
      filtered = filtered.filter(row => {
        const rowDate = new Date(row[dateToUse]);
        return rowDate >= start && rowDate <= end;
      });
    } else if (filters.dateFilterType === 'before' && filters.beforeDate) {
      const before = new Date(filters.beforeDate);
      filtered = filtered.filter(row => {
        const rowDate = new Date(row[dateToUse]);
        return rowDate < before;
      });
    } else if (filters.dateFilterType === 'after' && filters.afterDate) {
      const after = new Date(filters.afterDate);
      filtered = filtered.filter(row => {
        const rowDate = new Date(row[dateToUse]);
        return rowDate > after;
      });
    }

    // Sort by Company, RegionName, Project Number, and then Employee Number
    filtered.sort((a, b) => {
      // Sort by Company
      const companyA = (a.company || '').toLowerCase();
      const companyB = (b.company || '').toLowerCase();
      if (companyA !== companyB) {
        return companyA.localeCompare(companyB);
      }

      // Sort by Region Name
      const regionA = (a.regionName || '').toLowerCase();
      const regionB = (b.regionName || '').toLowerCase();
      if (regionA !== regionB) {
        return regionA.localeCompare(regionB);
      }

      // Sort by Project Number
      const projectA = (a.projectNumber || '').toLowerCase();
      const projectB = (b.projectNumber || '').toLowerCase();
      if (projectA !== projectB) {
        return projectA.localeCompare(projectB);
      }

      // If all above are equal, no further sorting needed here
      // (Employee number sorting happens within each job's CMs)
      return 0;
    });

    // Sort CMs within each job by Employee Number
    filtered.forEach(row => {
      if (row.cMs && row.cMs.length > 1) {
        row.cMs.sort((a, b) => {
          const empA = (a.employeeNumber || '').toLowerCase();
          const empB = (b.employeeNumber || '').toLowerCase();
          return empA.localeCompare(empB);
        });
      }
    });

    setFilteredRows(filtered);
    setSelectedRows(new Set()); // Clear selections when filters change
    setSelectAll(false);
  };

  useEffect(() => {
    applyFilters();
  }, [filters, rows, filterOptions]);

  const handleFilterChange = (field: keyof PayrollSearchFilters, value: string) => {
  let parsedValue: any = value;
  
  // Parse numeric fields
  if (field === 'companyId') {
    parsedValue = value === '' ? 0 : parseInt(value, 10);
  } else if (field === 'regionId') {
    if (value === '' || value === 'All') {
      parsedValue = value === '' ? 0 : 'All';
    } else {
      parsedValue = parseInt(value, 10);
    }
  }
  
  setFilters(prev => ({ ...prev, [field]: parsedValue }));
};

  const clearFilters = () => {
    setFilters({
      companyId: 0,
      regionId: 0,
      dateFilterType: 'none',
      month: '',
      startDate: '',
      endDate: '',
      beforeDate: '',
      afterDate: ''
    });
  };

  // Handle select all checkbox
  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      const allKeys = new Set<string>();
      filteredRows.forEach(row => {
        row.cMs.forEach(cm => {
          allKeys.add(getRowKey(row, cm));
        });
      });
      setSelectedRows(allKeys);
    } else {
      setSelectedRows(new Set());
    }
  };

  // Handle individual row checkbox
  const handleRowSelect = (job: JobData, cm: CMData, checked: boolean) => {
    const key = getRowKey(job, cm);
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(key);
      } else {
        newSet.delete(key);
      }
      return newSet;
    });
  };

  // Check if all visible rows are selected
  useEffect(() => {
    const totalVisibleRows = filteredRows.reduce((sum, row) => sum + row.cMs.length, 0);
    setSelectAll(totalVisibleRows > 0 && selectedRows.size === totalVisibleRows);
  }, [selectedRows, filteredRows]);

  const downloadReport = async () => {
    const selectedCount = selectedRows.size;
    const totalCount = filteredRows.reduce((sum, row) => sum + row.cMs.length, 0);
    
    let message = "Are you sure you want to generate the payroll report?";
    if (selectedCount > 0 && selectedCount < totalCount) {
      message = `You have selected ${selectedCount} out of ${totalCount} items. Only selected items will be included in the report. Continue?`;
    } else if (selectedCount === 0) {
      message = "No items are selected. All visible items will be included in the report. Continue?";
    }

    if (window.confirm(message)) {
      setLoadingReport(true);
      const formattedDate = formatDateTime();
      const filename = `CBA_${bonusType}_Payout_${formattedDate}.xlsx`;
      
      try {
        // Prepare the data to send
        const selectedData = selectedCount > 0 
          ? Array.from(selectedRows) 
          : null; // null means all visible rows

        await downloadFile(
          "/api/cba/payout-process/create-spreadsheet",
          filename,
          {
            PayrollSelectedKeys: selectedData,
            PayrollSearchFilters: filters
          }
        );

        // After successful download, refresh table and clear selections
        await loadTable();
        setSelectedRows(new Set());
        setSelectAll(false);

        dispatch({
          type: "Snackbar",
          payload: {
            show: true,
            message: "Selected records downloaded successfully.",
            severity: "success",
          },
        });
      } catch (error) {
        dispatch({
          type: "Snackbar",
          payload: {
            show: true,
            message: "Failed to generate report. Please try again.",
            severity: "error",
          },
        });
      } finally {
        setLoadingReport(false);
      }
    }
  };

  const validateFile = (file: File): string | null => {
    const allowedTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      return 'Please upload only Excel files (.xlsx or .xls)';
    }

    if (file.size > maxSize) {
      return 'File size must be less than 10MB';
    }

    return null;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const error = validateFile(file);
      
      if (error) {
        dispatch({
          type: "Snackbar",
          payload: {
            show: true,
            message: error,
            severity: "error",
          },
        });
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      setSelectedFile(file);
      setUploadModalOpen(true);
    }
  };

  const handleUploadConfirm = async () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append("File", selectedFile);
    setUploading(true);

    try {
      await httpClient.post(
        "/api/cba/payout-process/upload-paid-info-spreadsheet",
        formData,
        {
          headers: {
            BonusTypeId: bonusType === "Stage5Bonus" ? "1" : "2",
          },
        }
      );
      dispatch({
        type: "Snackbar",
        payload: {
          show: true,
          message: "File Uploaded Successfully",
          severity: "success",
        },
      });
      await loadTable();
    } catch (error) {
      dispatch({
        type: "Snackbar",
        payload: {
          show: true,
          message: "Failed to upload file. Please try again.",
          severity: "error",
        },
      });
    } finally {
      setUploading(false);
      setUploadModalOpen(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleUploadCancel = () => {
    setUploadModalOpen(false);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const loadTable = async () => {
    setLoading(true);
    try {
      const res = await httpClient.get<JobData[]>(
        "/api/cba/payout-details",
        {
          headers: {
            roleType: role,
            BonusTypeId: bonusType === "Stage5Bonus" ? "1" : "2",
          },
        }
      );

      if (res && res.data) {
        setRows(res.data);
      }
    } catch (error) {
      console.error("Failed to load table data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTable();
    loadFilterOptions();
  }, []);

  useEffect(() => {
    if (accounts[0].idTokenClaims?.roles && !isPayroll) {
      navigate("/");
    }
  }, [isPayroll, accounts, navigate]);

  const showInstructions = () => {
    alert(
      "How to use this page:\n\n" +
        "1. Use the 'Filter' button to filter data by company, region, or date.\n" +
        "2. Select specific rows using checkboxes to export only those items.\n" +
        "3. Use the 'Upload Report' button to upload a new payroll spreadsheet.\n" +
        "4. Click 'Generate Report' to download the payroll report (selected or all visible items).\n" +
        "5. Ensure all projects are approved before generating the final report."
    );
  };

  return (
    <main className="flex-grow p-4">
      <div className="bg-white shadow-xl rounded-lg overflow-hidden">
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center align-middle">
              <h2 className="text-xl font-semibold">Payroll Data</h2>
              <button
                onClick={showInstructions}
                className="ml-2 text-gray-500 hover:text-gray-700"
                title="How to use this page"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {selectedRows.size > 0 && (
                <span className="ml-4 text-sm text-blue-600 font-medium">
                  {selectedRows.size} item{selectedRows.size !== 1 ? 's' : ''} selected
                </span>
              )}
            </div>
            <div className="flex gap-2 items-center">
              <button
                className="bg-gray-500 text-white px-3 py-2 rounded-md flex items-center text-sm hover:brightness-150 transition-colors"
                onClick={() => setShowFilters(!showFilters)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                    clipRule="evenodd"
                  />
                </svg>
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>

              <label className="cursor-pointer bg-primary-500 text-white px-3 py-2 rounded-md flex items-center text-sm hover:brightness-150 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {uploading ? "Uploading..." : "Upload Report"}
                <input
                  type="file"
                  className="hidden"
                  accept=".xlsx"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  disabled={uploading}
                />
              </label>

              <button
                className="bg-primary-500 text-white px-3 py-2 rounded-md flex items-center text-sm hover:brightness-150 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={downloadReport}
                disabled={filteredRows.length === 0 || loadingReport}
              >
                {loadingReport ? (
                  <svg
                    className="animate-spin h-5 w-5 mr-3"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : null}
                Generate Report
              </button>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 p-4 bg-white rounded-md border border-gray-300">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Company Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company
                  </label>
                  <select
                    value={filters.companyId}
                    onChange={(e) => handleFilterChange('companyId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Companies</option>
                    {filterOptions.companies.map(company => (
                      <option key={company.companyId} value={company.companyId.toString()}>
                        {company.companyName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Region Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Region
                  </label>
                  <select
                    value={filters.regionId}
                    onChange={(e) => handleFilterChange('regionId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Regions</option>
                    {filterOptions.regions.map(region => (
                      <option 
                        key={region.regionId} 
                        value={region.regionId === -1 ? "All" : region.regionId.toString()}
                      >
                        {region.regionName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date Filter Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date Filter
                  </label>
                  <select
                    value={filters.dateFilterType}
                    onChange={(e) => handleFilterChange('dateFilterType', e.target.value as DateFilterType)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="none">No Date Filter</option>
                    <option value="month">Specific Month</option>
                    <option value="dateRange">Date Range</option>
                    <option value="before">Before Date</option>
                    <option value="after">After Date</option>
                  </select>
                </div>

                {/* Date Input Fields */}
                {filters.dateFilterType === 'month' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Month
                    </label>
                    <input
                      type="month"
                      value={filters.month}
                      onChange={(e) => handleFilterChange('month', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}

                {filters.dateFilterType === 'dateRange' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={filters.startDate}
                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={filters.endDate}
                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </>
                )}

                {filters.dateFilterType === 'before' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Before Date
                    </label>
                    <input
                      type="date"
                      value={filters.beforeDate}
                      onChange={(e) => handleFilterChange('beforeDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}

                {filters.dateFilterType === 'after' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      After Date
                    </label>
                    <input
                      type="date"
                      value={filters.afterDate}
                      onChange={(e) => handleFilterChange('afterDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Clear Filters
                </button>
                <span className="text-sm text-gray-600 self-center">
                  Showing {filteredRows.reduce((sum, row) => sum + row.cMs.length, 0)} of {rows.reduce((sum, row) => sum + row.cMs.length, 0)} items
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-200">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Job Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Employee Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  CM Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Bonus Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  {bonusType === "Stage5Bonus" ? "Stage 5 Date" : "Close Date"}
                </th>                                
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Company
                </th>                                
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Region
                </th>                                
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto"></div>
                  </td>
                </tr>
              ) : filteredRows.length > 0 ? (
                filteredRows.flatMap((row) =>
                  row.cMs.map((cm, index) => {
                    const rowKey = getRowKey(row, cm);
                    const isSelected = selectedRows.has(rowKey);
                    
                    return (
                      <tr
                        key={rowKey}
                        className={`hover:bg-gray-100 ${isSelected ? 'bg-blue-50' : ''}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => handleRowSelect(row, cm, e.target.checked)}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                        </td>
                        {index === 0 && (
                          <td
                            rowSpan={row.cMs.length}
                            className="px-6 py-4 whitespace-nowrap"
                          >
                            {row.jobNumber}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {cm.employeeNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{cm.cm}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {formatCurrency(cm.bonusAmount)}
                        </td>
                        {index === 0 && (
                          <td
                            rowSpan={row.cMs.length}
                            className="px-6 py-4 whitespace-nowrap"
                          >
                            {bonusType === "Stage5Bonus"
                              ? new Date(row.stage5Date).toLocaleDateString()
                              : new Date(row.closeDate).toLocaleDateString()}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {row.company || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {row.regionName || 'N/A'}
                        </td>
                      </tr>
                    );
                  })
                )
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    <div className="py-20">
                      <h6 className="text-xl font-semibold">
                        {rows.length > 0 ? 'No items match the current filters.' : 'No items in payroll report.'}
                      </h6>
                      <p className="text-gray-500 mt-2">
                        {rows.length > 0 
                          ? 'Try adjusting your filters to see more results.' 
                          : 'Please approve more projects before returning.'}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      <UploadModal
        open={uploadModalOpen}
        file={selectedFile}
        uploading={uploading}
        onConfirm={handleUploadConfirm}
        onCancel={handleUploadCancel}
      />
    </main>
  );
}